import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Webinar } from 'models/webinars';
import { selectLocale } from 'models/localization';
import ShowMore from 'components/utils/ShowMore';
import { isSupportedStream } from 'utils/streamableLinkUtils';
import QuillRichtextDisplay from 'components/QuillRichtextDisplay';
import WebinarRootContainer from './components/WebinarRootContainer';
import WatchNowButton from './components/WatchNowButton';
import SpeakerList from './SpeakerList';
import Routes from '../../Routes';
import { BREAKPOINT } from './constants';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  & > * {
    width: 100%;
  }
`;

const TopTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TopRightActionButtonWrapper = styled.div`
  width: 182px;
  flex: 0 0 auto;

  @media (max-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const BottomActionButtonWrapper = styled.div`
  margin-top: 16px;
  display: none;

  @media (max-width: ${BREAKPOINT}px) {
    display: block;
  }
`;

const NameContainer = styled(Link)`
  display: inline-block;
  font-style: normal;
  font-weight: bold;
  color: ${({ color }) => color};
  text-decoration: none;

  @media (min-width: ${BREAKPOINT}px) {
    font-size: 24px;
    line-height: 29px;
    max-height: 58px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    font-size: 18px;
    line-height: 22px;
    max-height: 22px;
  }
`;

const UnsupportedWebinarNameContainer = styled.a`
  display: inline-block;
  font-style: normal;
  font-weight: bold;
  color: ${({ color }) => color};
  text-decoration: none;

  @media (min-width: ${BREAKPOINT}px) {
    font-size: 24px;
    line-height: 29px;
    max-height: 58px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    font-size: 18px;
    line-height: 22px;
    max-height: 22px;
  }
`;

const WebinarDescriptionContainer = styled.div`
  width: 100%;
  display: inline-block;
  font-size: 16px;
  line-height: 22px;
  overflow: hidden;
  margin: 16px 0 0 0;

  & > *:first-child {
    margin-top: 0;
  }
`;

const WebinarItem = ({
  webinar, webinar: { translations }, className,
}: { webinar: Webinar; className: string }) => {
  const locale = useSelector(selectLocale);
  const translation = translations[locale];
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const supportedStream = useMemo(() => Boolean(translation?.url && isSupportedStream(translation.url)), [translation?.url]);

  return (
    <WebinarRootContainer className={className}>
      <ContentContainer>
        <div>
          <TopTitleContainer>
            <div>
              {supportedStream ? (
                <NameContainer
                  to={Routes.webinarSession(webinar.id)}
                  color={themeColor || DEFAULT_THEME_COLOR}
                >
                  {translation?.title}
                </NameContainer>
              ) : (
                <UnsupportedWebinarNameContainer
                  target="_blank"
                  rel="noopener noreferrer"
                  href={translation?.url}
                  color={themeColor || DEFAULT_THEME_COLOR}
                >
                  {translation?.title}
                </UnsupportedWebinarNameContainer>
              )}
            </div>
            {
              // eslint-disable-next-line
              (translation?.url?.trim().length > 0 || translation?.cloudflare_stream_id?.trim().length > 0) && (
              <TopRightActionButtonWrapper>
                <WatchNowButton
                  kind={webinar.kind}
                  id={webinar.id}
                  name={translation?.title}
                  url={supportedStream ? Routes.webinarSession(webinar.id) : translation?.url}
                  supportedStream={supportedStream}
                />
              </TopRightActionButtonWrapper>
              )
            }
          </TopTitleContainer>
          <WebinarDescriptionContainer>
            <ShowMore maxHeight={150}>
              <QuillRichtextDisplay html={translation?.description} />
            </ShowMore>
          </WebinarDescriptionContainer>
        </div>
        <div>
          <SpeakerList
            webinarId={webinar.id}
            speakers={webinar.speakers}
          />
          {
            // eslint-disable-next-line
            (translation?.url?.trim().length > 0 || translation?.cloudflare_stream_id?.trim().length > 0) && (
              <BottomActionButtonWrapper>
                <WatchNowButton
                  id={webinar.id}
                  name={translation?.title}
                  url={supportedStream ? Routes.webinarSession(webinar.id) : translation?.url}
                  supportedStream={supportedStream}
                />
              </BottomActionButtonWrapper>
            )
          }
        </div>
      </ContentContainer>
    </WebinarRootContainer>
  );
};

export default WebinarItem;
